/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { CreatePayrollRunSchema, type CreatePayrollRunInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { useCreatePayrollRun } from '../../lib/queries/payroll.js';

interface PayrollRunFormProps {
  readonly onDone: () => void;
}

/** "New Payroll Run" form (Figma). Validated by shared `CreatePayrollRunSchema`. */
export function PayrollRunForm({ onDone }: PayrollRunFormProps): JSX.Element {
  const { t } = useTranslation();
  const create = useCreatePayrollRun();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreatePayrollRunInput>({ resolver: zodResolver(CreatePayrollRunSchema) });

  const submit = useCallback(
    async (values: CreatePayrollRunInput): Promise<void> => {
      await create.mutateAsync({
        periodLabel: values.periodLabel,
        periodStart: values.periodStart,
        periodEnd: values.periodEnd,
        ...(values.notes !== undefined && values.notes !== '' ? { notes: values.notes } : {}),
      });
      onDone();
    },
    [create, onDone],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <FormField
        id="run-period"
        type="text"
        label={t('payroll.form.period')}
        placeholder={t('payroll.form.periodHint')}
        autoComplete="off"
        error={errors.periodLabel?.message}
        registration={register('periodLabel')}
      />
      <FormField
        id="run-start"
        type="date"
        label={t('payroll.form.start')}
        autoComplete="off"
        error={errors.periodStart?.message}
        registration={register('periodStart')}
      />
      <FormField
        id="run-end"
        type="date"
        label={t('payroll.form.end')}
        autoComplete="off"
        error={errors.periodEnd?.message}
        registration={register('periodEnd')}
      />
      <FormField
        id="run-notes"
        type="text"
        label={t('payroll.form.notes')}
        autoComplete="off"
        error={errors.notes?.message}
        registration={register('notes')}
      />

      {create.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || create.isPending}>
          {t('payroll.form.submit')}
        </Button>
      </div>
    </form>
  );
}
